import type { ComponentPropsWithRef, ReactNode } from 'react';
import { forwardRef } from 'react';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils';

// Components
import FormFieldMessage from '@/components/base/FormFieldMessage';

const DEFAULT = 'default';
const SEARCH = 'search';
const SEARCH_DARKEN = 'search_darken';
const SEARCH_SELECT = 'search_select';
const DATEPICKER = 'datepicker';
const DATEPICKER_NO_BG = 'datepicker_no_background';
const EDITABLE_TEXT = 'editable_text';
const EDITABLE_TEXT_XL = 'editable_text_xl';
const EDITABLE_TEXT_SMALL = 'editable_text_small';
const DROPDOWN = 'dropdown';
export const MESSAGE = 'message';
const ADDONS_NUMBER = 'addons_number';
const INSPIRATION = 'inspiration';
const DEFAULT_NO_BACKGROUND = 'default_no_background';
const SMALL_ROUNDED_NO_BACKGROUND = 'small_rounded_no_background';

type StyleInputType =
    | typeof DEFAULT
    | typeof SEARCH
    | typeof SEARCH_DARKEN
    | typeof SEARCH_SELECT
    | typeof DATEPICKER
    | typeof DATEPICKER_NO_BG
    | typeof EDITABLE_TEXT
    | typeof EDITABLE_TEXT_SMALL
    | typeof EDITABLE_TEXT_XL
    | typeof DROPDOWN
    | typeof MESSAGE
    | typeof ADDONS_NUMBER
    | typeof INSPIRATION
    | typeof DEFAULT_NO_BACKGROUND
    | typeof SMALL_ROUNDED_NO_BACKGROUND;

type StyleObjectEntryType = {
    container: string;
    input: string;
};

const defaultStyles: StyleObjectEntryType = {
    container:
        'bg-white dark:bg-icoDarkMode-anthracite text-icoGray-400 dark:text-icoDarkMode-wolf space-x-3 px-4 max-h-12 rounded-md',
    input: 'placeholder-icoGray-400 dark:placeholder-icoDarkMode-wolf text-sm font-GSemiBold py-4'
};

const styleInput: Record<StyleInputType, StyleObjectEntryType> = {
    [DROPDOWN]: defaultStyles,
    [DEFAULT]: defaultStyles,
    [SEARCH]: {
        container:
            'bg-icoGray-100 dark:bg-icoDarkMode-anthracite text-icoGray-500 dark:text-icoDarkMode-wolf space-x-3 px-4 max-h-12 rounded-md',
        input: 'placeholder-icoGray-500 text-sm font-GSemiBold py-5'
    },
    [SEARCH_DARKEN]: {
        container:
            'bg-icoGray-100 dark:bg-icoDarkMode-deepBlack text-icoGray-500 dark:text-icoDarkMode-wolf space-x-3 px-4 max-h-12 rounded-md',
        input: 'placeholder-icoGray-500 text-sm font-GSemiBold py-5'
    },
    [SEARCH_SELECT]: {
        container:
            'bg-icoGray-100 dark:bg-icoDarkMode-deepBlack text-icoGray-500 dark:text-icoDarkMode-wolf space-x-2 px-4 max-h-12 rounded-md',
        input: 'placeholder-icoGray-500 text-sm font-GSemiBold py-2.5'
    },
    [DATEPICKER]: {
        container:
            'bg-white dark:bg-icoDarkMode-anthracite text-icoGray-500 dark:text-icoDarkMode-wolf space-x-3 px-5 max-h-12 rounded-md',
        input: 'placeholder-icoGray-400 dark:placeholder-icoDarkMode-wolf text-sm font-GSemiBold cursor-pointer py-5'
    },
    [INSPIRATION]: {
        container:
            'bg-transparent text-icoGray-500 dark:text-icoDarkMode-wolf space-x-3 px-5 max-h-12 rounded-md',
        input: 'placeholder-icoGray-400 dark:placeholder-icoDarkMode-wolf text-sm font-GSemiBold cursor-pointer disabled:cursor-not-allowed py-5'
    },
    [DATEPICKER_NO_BG]: {
        container: 'text-icoGray-500 dark:text-icoDarkMode-wolf space-x-3 px-5 max-h-12 rounded-md',
        input: 'placeholder-icoGray-400 dark:placeholder-icoDarkMode-wolf text-sm font-GSemiBold cursor-pointer py-5'
    },
    [EDITABLE_TEXT]: {
        container: 'p-0',
        input: 'placeholder-icoGray-500 text-3.5xl font-GBold pt-0 pb-1'
    },
    [EDITABLE_TEXT_XL]: {
        container: 'p-0',
        input: 'placeholder-icoGray-500 text-xl font-GBold pt-0 pb-1'
    },
    [EDITABLE_TEXT_SMALL]: {
        container: 'p-0',
        input: 'placeholder-icoGray-500 text-icoGray-500 dark:text-icoDarkMode-wolf text-xs font-GMedium'
    },
    [MESSAGE]: {
        container: 'bg-white dark:bg-icoDarkMode-anthracite text-icoGray-400 pl-10 pr-8 rounded-md',
        input: 'placeholder-icoGray-500 text-sm font-GMedium py-5 h-20 outline-none'
    },
    [ADDONS_NUMBER]: {
        container:
            'bg-white dark:bg-icoDarkMode-anthracite text-icoGray-400 dark:text-icoDarkMode-wolf space-x-3 px-14 max-h-12 rounded-md',
        input: 'placeholder-icoGray-400 font-GSemiBold text-center py-5 appearance-none'
    },
    [DEFAULT_NO_BACKGROUND]: {
        container: 'text-icoGray-400 dark:text-icoDarkMode-wolf space-x-3 px-4 max-h-12 rounded-md',
        input: defaultStyles.input
    },
    [SMALL_ROUNDED_NO_BACKGROUND]: {
        container: 'text-xs text-icoGray-400 dark:text-icoDarkMode-wolf space-x-2 px-2.5',
        input: 'placeholder-icoGray-400 dark:placeholder-icoDarkMode-wolf text-xs py-2.5'
    }
};

const containerClass =
    'relative border-0 w-full flex items-center border transition transition-all';
const inputClass =
    'relative peer z-1 px-0 bg-transparent border-none w-full focus-visible:outline-none';

export type InputProps = ComponentPropsWithRef<'input'> & {
    noBorder?: boolean;
    noBorderShadow?: boolean;
    beforeInput?: ReactNode;
    afterInput?: ReactNode;
    inputStyle?: keyof typeof styleInput;
    inputClassName?: string;
    error?: boolean | string;
    valid?: boolean | string;
    charsCount?: number;
    minMaxReached?: boolean;
};

const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    const {
        type = 'text',
        noBorder = false,
        noBorderShadow = false,
        beforeInput,
        afterInput,
        className,
        inputStyle = 'default',
        inputClassName,
        error,
        valid,
        charsCount,
        minMaxReached,
        ...restProps
    } = props;
    const { t } = useTranslation();

    return (
        <>
            <div
                className={clsx(
                    containerClass,
                    styleInput[inputStyle]?.container ?? styleInput['default'].container,
                    className
                )}>
                {beforeInput && (
                    <div
                        className={clsx('z-1 flex items-center', {
                            'pointer-events-none': restProps.disabled
                        })}>
                        {beforeInput}
                    </div>
                )}
                <div className="flex-1">
                    <input
                        type={type}
                        ref={ref}
                        aria-describedby={`${restProps.id}_error`}
                        maxLength={restProps.maxLength}
                        className={classNames(
                            inputClass,
                            styleInput[inputStyle]?.input ?? styleInput['default'].input,
                            inputClassName,
                            {
                                'focus:ring-0': inputStyle !== DATEPICKER,
                                'focus:ring-transparent':
                                    inputStyle === DATEPICKER || inputStyle === INSPIRATION,
                                'text-icoRed': error, // error
                                'text-icoGreen': valid && !error, // valid
                                'text-icoBlack dark:text-white': !error && !valid && !minMaxReached, // default
                                'cursor-not-allowed text-icoGray-400': restProps.disabled,
                                'pointer-events-none': restProps.readOnly,
                                'text-icoGray-400 dark:text-icoDarkMode-beton':
                                    minMaxReached && !error
                            }
                        )}
                        {...restProps}
                    />
                    <div
                        className={clsx('absolute inset-0 z-0 w-full border transition-all', {
                            'rounded-md':
                                inputStyle !== DROPDOWN &&
                                inputStyle !== SMALL_ROUNDED_NO_BACKGROUND,
                            'rounded-full': inputStyle === SMALL_ROUNDED_NO_BACKGROUND,
                            'peer-focus:ring-2':
                                inputStyle !== DATEPICKER &&
                                inputStyle !== EDITABLE_TEXT &&
                                inputStyle !== EDITABLE_TEXT_XL &&
                                inputStyle !== INSPIRATION,
                            'border-0 peer-focus:ring-transparent':
                                inputStyle === DATEPICKER && noBorder && noBorderShadow,
                            'peer-focus:border-icoBlue peer-focus:ring-icoBlue-200':
                                !error &&
                                !valid &&
                                inputStyle !== EDITABLE_TEXT &&
                                inputStyle !== EDITABLE_TEXT_XL &&
                                inputStyle !== INSPIRATION, // default
                            'border-icoRed peer-focus:border-icoRed peer-focus:ring-icoRed-200':
                                error, // error
                            'border-icoGreen peer-focus:border-icoGreen peer-focus:ring-icoGreen-200':
                                valid && !error, // valid
                            'border-icoGray-300 dark:border-icoDarkMode-beton':
                                !error && !valid && !noBorder && !noBorderShadow, // default with border
                            'border-transparent shadow-icoM':
                                !error &&
                                !valid &&
                                noBorder &&
                                !noBorderShadow &&
                                inputStyle !== SEARCH &&
                                inputStyle !== SEARCH_DARKEN &&
                                inputStyle !== SEARCH_SELECT &&
                                inputStyle !== EDITABLE_TEXT &&
                                inputStyle !== EDITABLE_TEXT_XL, // default without border,
                            'border-transparent':
                                !error &&
                                !valid &&
                                ((noBorder &&
                                    (inputStyle === SEARCH ||
                                        inputStyle === SEARCH_SELECT ||
                                        inputStyle === SEARCH_DARKEN)) || // specific to search input
                                    noBorderShadow),
                            'cursor-not-allowed bg-icoGray-300 dark:bg-icoDarkMode-beton':
                                restProps.disabled &&
                                inputStyle !== EDITABLE_TEXT &&
                                inputStyle !== EDITABLE_TEXT_XL &&
                                inputStyle !== INSPIRATION, // disabled,
                            'cursor-not-allowed':
                                restProps.disabled &&
                                (inputStyle === EDITABLE_TEXT || inputStyle === EDITABLE_TEXT_XL), // disabled,
                            'rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-icoGray-300':
                                inputStyle === EDITABLE_TEXT || inputStyle === EDITABLE_TEXT_XL,
                            hidden: inputStyle === EDITABLE_TEXT_SMALL,
                            'rounded-bl-md rounded-br-md border-t-0': inputStyle === DROPDOWN
                        })}
                    />
                </div>
                {afterInput && (
                    <div className={clsx('z-1', { 'pointer-events-none': restProps.disabled })}>
                        {afterInput}
                    </div>
                )}
            </div>
            {error && typeof error === 'string' ? (
                <FormFieldMessage id={`${restProps.id ?? ''}_error`}>{error}</FormFieldMessage>
            ) : null}
            {valid && !error && typeof valid === 'string' ? (
                <FormFieldMessage type="valid">{valid}</FormFieldMessage>
            ) : null}
            {charsCount || charsCount === 0 ? (
                <p className="mt-1 text-right font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {t('commons:characters_left', {
                        characters:
                            restProps.maxLength - charsCount < 0
                                ? 0
                                : restProps.maxLength - charsCount
                    })}
                </p>
            ) : null}
        </>
    );
});

Input.displayName = 'Input';

export default Input;
